import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Answer, Education } from '@/types/workerInfo.types';
import { colors, spacing } from '@/theme';
import { GraduationCap, Plus, Trash2, Edit2, Check, X } from 'lucide-react';

interface EducationInputProps {
  answer: Answer;
  onBlur: (value: { educationValue: Education[] }) => void;
  disabled?: boolean;
}

export function EducationInput({ answer, onBlur, disabled }: EducationInputProps) {
  const [educations, setEducations] = useState<Education[]>(answer.educationValue || []);
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [formData, setFormData] = useState<Partial<Education>>({
    institution: '',
    degree: '',
    field: '',
    startDate: '',
    endDate: '',
    current: false,
    description: '',
  });

  const handleSubmit = () => {
    if (!formData.institution || !formData.degree || !formData.startDate || !formData.educationType || formData.hasCertificate === undefined) return;
    let newEducations: Education[];

    if (editingIndex !== null) {
      newEducations = [...educations];
      newEducations[editingIndex] = formData as Education;
    } else {
      newEducations = [...educations, formData as Education];
    }

    setEducations(newEducations);
    onBlur({ educationValue: newEducations });
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
    resetForm();
  };

  const handleDelete = (index: number) => {
    const newEducations = educations.filter((_, i) => i !== index);
    setEducations(newEducations);
    onBlur({ educationValue: newEducations });
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setFormData(educations[index]);
    setIsAdding(true);
  };

  const resetForm = () => {
    setIsAdding(false);
    setEditingIndex(null);
    setFormData({
      institution: '',
      degree: '',
      field: '',
      startDate: '',
      endDate: '',
      current: false,
      description: '',
      educationType: undefined,
      hasCertificate: false,
      certificateFileId: undefined,
      certificateName: undefined,
      certificateUrl: undefined,
      certificateMimeType: undefined,
      certificateSize: undefined,
    });
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: spacing[4], gap: spacing[2] }}>
        <div>
          <label style={{ fontSize: 16, fontWeight: 600, color: colors.gray[900], display: 'flex', alignItems: 'center', gap: spacing[2], marginBottom: spacing[1] }}>
            <GraduationCap size={16} color={colors.primary[500]} />
            {answer.question.question}
            {answer.question.isRequired && <span style={{ color: colors.error?.[500] || '#ef4444', marginLeft: 4 }}>*</span>}
          </label>
          {answer.question.description && <p style={{ fontSize: 14, color: colors.gray[600] }}>{answer.question.description}</p>}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2] }}>
          <AnimatePresence>
            {isSaved && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                style={{ display: 'flex', alignItems: 'center', gap: spacing[1], color: colors.success?.[500] || '#10b981', fontSize: 12, fontWeight: 600 }}
              >
                <Check size={14} />
                <span>Guardado</span>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={() => setIsAdding(true)}
            disabled={disabled}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing[1],
              padding: `${spacing[2]} ${spacing[3]}`,
              backgroundColor: colors.primary[500],
              color: colors.white,
              border: 'none',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            <Plus size={14} />
            Agregar
          </motion.button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
        {educations.map((edu, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ padding: spacing[4], backgroundColor: colors.gray[50], borderRadius: 14, border: `1px solid ${colors.gray[200]}` }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: spacing[2], gap: spacing[2] }}>
              <div>
                <h4 style={{ fontSize: 15, fontWeight: 700, color: colors.gray[900] }}>{edu.degree} en {edu.field}</h4>
                <p style={{ fontSize: 13, color: colors.primary[600], fontWeight: 600 }}>{edu.institution}</p>
              </div>
              <div style={{ display: 'flex', gap: spacing[1] }}>
                <button onClick={() => handleEdit(index)} style={{ padding: spacing[1], background: 'transparent', border: 'none', color: colors.gray[500], cursor: 'pointer' }}>
                  <Edit2 size={14} />
                </button>
                <button onClick={() => handleDelete(index)} style={{ padding: spacing[1], background: 'transparent', border: 'none', color: colors.error?.[500] || '#ef4444', cursor: 'pointer' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <p style={{ fontSize: 12, color: colors.gray[500], marginBottom: spacing[2] }}>
              {edu.startDate} - {edu.current ? 'Actual' : edu.endDate}
            </p>
            {edu.educationType && <p style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1] }}><strong>Tipo:</strong> {edu.educationType}</p>}
            {edu.hasCertificate !== undefined && <p style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1] }}><strong>Certificado:</strong> {edu.hasCertificate ? 'Sí' : 'No'}</p>}
            <p style={{ fontSize: 13, color: colors.gray[700], lineHeight: 1.5 }}>{edu.description}</p>
          </motion.div>
        ))}

        {educations.length === 0 && !isAdding && (
          <div style={{ padding: spacing[6], backgroundColor: colors.gray[50], borderRadius: 14, border: `1px dashed ${colors.gray[300]}`, textAlign: 'center' }}>
            <GraduationCap size={28} color={colors.gray[400]} style={{ marginBottom: spacing[2] }} />
            <p style={{ fontSize: 13, color: colors.gray[600] }}>No has agregado formacion academica aun</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {isAdding && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            style={{
              marginTop: spacing[4],
              padding: spacing[4],
              backgroundColor: colors.gray[50],
              borderRadius: 16,
              border: `1px solid ${colors.primary[300]}`,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: spacing[4] }}>
              <h4 style={{ fontSize: 15, fontWeight: 700, color: colors.primary[700] }}>{editingIndex !== null ? 'Editar educacion' : 'Nueva educacion'}</h4>
              <button onClick={resetForm} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <X size={16} color={colors.gray[500]} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: spacing[3], marginBottom: spacing[3] }}>
              <div>
                <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>Institucion *</label>
                <input type="text" value={formData.institution} onChange={(e) => setFormData({ ...formData, institution: e.target.value })} style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14 }} placeholder="Centro educativo" />
              </div>
              <div>
                <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>Titulo *</label>
                <input type="text" value={formData.degree} onChange={(e) => setFormData({ ...formData, degree: e.target.value })} style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14 }} placeholder="Licenciatura, tecnico..." />
              </div>
            </div>

            <div style={{ marginBottom: spacing[3] }}>
              <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>Campo de estudio *</label>
              <input type="text" value={formData.field} onChange={(e) => setFormData({ ...formData, field: e.target.value })} style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14 }} placeholder="Administracion, sistemas..." />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: spacing[3], marginBottom: spacing[3] }}>
              <div>
                <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>Fecha inicio *</label>
                <input type="date" value={formData.startDate} onChange={(e) => setFormData({ ...formData, startDate: e.target.value })} style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14 }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>Fecha fin</label>
                <input type="date" value={formData.endDate || ''} onChange={(e) => setFormData({ ...formData, endDate: e.target.value })} disabled={formData.current} style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14, opacity: formData.current ? 0.5 : 1 }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: spacing[1], fontSize: 13, color: colors.gray[700] }}>
                  <input type="checkbox" checked={formData.current} onChange={(e) => setFormData({ ...formData, current: e.target.checked, endDate: '' })} />
                  Estudio actual
                </label>
              </div>
            </div>

            <div style={{ marginBottom: spacing[4] }}>
              <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>Descripcion</label>
              <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14, minHeight: 78, resize: 'vertical' }} placeholder="Logros, enfoque o especializacion" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: spacing[3], marginBottom: spacing[3] }}>
              <div>
                <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>Tipo de educación *</label>
                <select value={formData.educationType || ''} onChange={(e) => setFormData({ ...formData, educationType: e.target.value as any })} style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14 }}>
                  <option value="">Seleccionar...</option>
                  <option value="primaria">Primaria</option>
                  <option value="secundaria">Secundaria</option>
                  <option value="tecnica">Técnica</option>
                  <option value="tecnologo">Tecnólogo</option>
                  <option value="pregrado">Pregrado</option>
                  <option value="postgrado">Postgrado</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>¿Tienes certificado? *</label>
                <select value={formData.hasCertificate === undefined ? '' : formData.hasCertificate.toString()} onChange={(e) => setFormData({ ...formData, hasCertificate: e.target.value === 'true' })} style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14 }}>
                  <option value="">Seleccionar...</option>
                  <option value="true">Sí</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>

            {formData.hasCertificate && (
              <div style={{ marginBottom: spacing[4] }}>
                <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>Certificado</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      // Aquí iría la lógica para subir el archivo
                      // Por ahora solo guardamos la información básica
                      setFormData({
                        ...formData,
                        certificateName: file.name,
                        certificateMimeType: file.type,
                        certificateSize: file.size,
                        // En un caso real, aquí subiríamos el archivo y obtendríamos la URL
                        certificateUrl: URL.createObjectURL(file),
                      });
                    }
                  }}
                  style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14 }}
                />
                <p style={{ fontSize: 11, color: colors.gray[500], marginTop: spacing[1] }}>Formatos permitidos: PDF, JPG, PNG. Máximo 5MB</p>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: spacing[2] }}>
              <button onClick={resetForm} style={{ padding: `${spacing[2]} ${spacing[3]}`, backgroundColor: 'transparent', border: `1px solid ${colors.gray[300]}`, borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                Cancelar
              </button>
              <button onClick={handleSubmit} style={{ padding: `${spacing[2]} ${spacing[3]}`, backgroundColor: colors.primary[500], color: colors.white, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {editingIndex !== null ? 'Actualizar' : 'Agregar'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
